import React from 'react';
import { ReportTheme } from '../../types';

interface TopLandingPagesTableWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const TopLandingPagesTableWidget: React.FC<TopLandingPagesTableWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('TopLandingPagesTableWidget raw data:', data);

  // Process the raw API data internally for Top Landing Pages
  const processData = () => {
    // Handle the actual data structure: { success: true, data: {...} }
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('TopLandingPagesTableWidget: Invalid data structure - missing data.data object');
      return null;
    }

    const rawData = data.data;
    const entries = Object.entries(rawData);
    
    console.log('TopLandingPagesTableWidget: Raw data entries:', entries.length);
    console.log('TopLandingPagesTableWidget: Sample entry:', entries[0]);
    
    if (entries.length === 0) {
      console.warn('TopLandingPagesTableWidget: No data entries found');
      return null;
    }

    // Filter data by date range if dates are provided
    let filteredEntries = entries;
    if (report_start_date && report_end_date) {
      const startDate = new Date(report_start_date);
      const endDate = new Date(report_end_date);
      
      filteredEntries = entries.filter(([dateKey, dayData]: [string, any]) => {
        if (!dayData || typeof dayData !== 'object') {
          return false;
        }
        // Convert dateKey (e.g., "20240912") to Date object
        const year = parseInt(dateKey.substring(0, 4));
        const month = parseInt(dateKey.substring(4, 6)) - 1; // Month is 0-indexed
        const day = parseInt(dateKey.substring(6, 8));
        const entryDate = new Date(year, month, day);
        
        return entryDate >= startDate && entryDate <= endDate;
      });
      
      
    }
    
    if (filteredEntries.length === 0) {
      console.warn('TopLandingPagesTableWidget: No data entries found in date range');
      return null;
    }

    // Aggregate page data across all dates
    const pageTotals: { [key: string]: { sessions: number; pageviews: number; bounceRate: number; count: number; bounceRateCount: number } } = {};

    filteredEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each page in the day's data
      Object.entries(dayData).forEach(([pageKey, pageData]: [string, any]) => {
        if (pageData && typeof pageData === 'object') {
          const sessions = pageData.sessions || 0;
          const pageviews = pageData.pageviews || 0;
          const bounceRate = pageData.bounce_rate || 0;
          const count = pageData.count || 0;

          if (!pageTotals[pageKey]) {
            pageTotals[pageKey] = { sessions: 0, pageviews: 0, bounceRate: 0, count: 0, bounceRateCount: 0 };
          }

          pageTotals[pageKey].sessions += sessions;
          pageTotals[pageKey].pageviews += pageviews;
          pageTotals[pageKey].count += count;
          // For bounce rate, we'll calculate the weighted average
          pageTotals[pageKey].bounceRate += bounceRate * sessions;
          pageTotals[pageKey].bounceRateCount += sessions;
        }
      });
    });

    // Convert to table data format
    const tableData = Object.entries(pageTotals)
      .map(([pageKey, pageData]) => ({
        page: pageKey,
        sessions: pageData.sessions,
        pageviews: pageData.pageviews,
        bounceRate: pageData.bounceRateCount > 0 
          ? `${((pageData.bounceRate / pageData.bounceRateCount) * 100).toFixed(1)}%`
          : '0.0%',
        count: pageData.count
      }))
      .sort((a, b) => b.sessions - a.sessions) // Sort by sessions descending
      .slice(0, 10); // Top 10

    console.log('TopLandingPagesTableWidget: Processed table data:', tableData);
    return tableData.length > 0 ? tableData : null;
  };

  const processedData = processData();
  if (!processedData || processedData.length === 0) {
    console.log('TopLandingPagesTableWidget: No processed data available');
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">No landing page data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-auto h-full">
      <table className={`w-full text-sm ${
        reportTheme.theme === 'light' ? 'text-gray-900' : 'text-foreground'
      }`}>
        <thead>
          <tr className={`border-b ${
            reportTheme.theme === 'light' ? 'border-gray-200' : 'border-border'
          }`}>
            <th className={`text-left p-2 ${
              reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
            }`}>Page</th>
            <th className={`text-right p-2 ${
              reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
            }`}>Sessions</th>
            <th className={`text-right p-2 ${
              reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
            }`}>Page Views</th>
            <th className={`text-right p-2 ${
              reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
            }`}>Bounce Rate</th>
            <th className={`text-right p-2 ${
              reportTheme.theme === 'light' ? 'text-gray-700' : 'text-foreground'
            }`}>Count</th>
          </tr>
        </thead>
        <tbody>
          {processedData.map((row, index) => (
            <tr key={index} className={`border-b ${
              reportTheme.theme === 'light' ? 'border-gray-100' : 'border-border'
            }`}>
              <td className="p-2">
                <div className="max-w-xs truncate" title={row.page}>
                  {row.page}
                </div>
              </td>
              <td className="p-2 text-right">{row.sessions.toLocaleString()}</td>
              <td className="p-2 text-right">{row.pageviews.toLocaleString()}</td>
              <td className="p-2 text-right">{row.bounceRate}</td>
              <td className="p-2 text-right">{row.count.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
